// =============================================================================
// Utility helpers used across the platform UI
// =============================================================================

export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/* ── Currency / Number ─────────────────────────────────────────────────────── */

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/* ── Dates ─────────────────────────────────────────────────────────────────── */

export function formatDate(d: string | Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(d));
}

export function formatRelative(d: string | Date): string {
  const ms  = Date.now() - new Date(d).getTime();
  const min = Math.floor(ms / 60_000);
  const hr  = Math.floor(ms / 3_600_000);
  const day = Math.floor(ms / 86_400_000);
  if (min < 1)  return 'Just now';
  if (min < 60) return `${min}m ago`;
  if (hr < 24)  return `${hr}h ago`;
  if (day < 7)  return `${day}d ago`;
  return formatDate(d);
}

/** Format an MB value across KB / MB / GB so a tenant with 0.78 MB
 *  doesn't read as "0 MB" or "1 MB". Tiers:
 *    < 1 MB   →  KB  (e.g. "780 KB")
 *    < 100 MB →  MB with up to 2 decimals (e.g. "1.13 MB")
 *    < 1 GB   →  MB rounded (e.g. "234 MB")
 *    >= 1 GB  →  GB with 2 decimals (e.g. "1.45 GB")
 */
export function formatStorage(mb: number): string {
  const v = Number(mb);
  if (!Number.isFinite(v)) return '0 KB';
  if (v < 1)    return `${Math.round(v * 1024)} KB`;
  if (v < 100)  return `${(+v.toFixed(2))} MB`;
  if (v < 1024) return `${Math.round(v)} MB`;
  return `${(v / 1024).toFixed(2)} GB`;
}

/* ── Slugs ─────────────────────────────────────────────────────────────────── */

export function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
}

/* ── Misc ──────────────────────────────────────────────────────────────────── */

export function generateVerifyToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = 'unravel_verify_';
  for (let i = 0; i < 24; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}
